"use client";

import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Info, Ban, Check } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';
import { getSliderColor } from '@/lib/slider-color';
import { Question, Phase2Question, generateShortQuizQuestions, generateLongQuizQuestions, generateGroupQuizQuestions, getTiebreakerQuestionsAsync, getPhase2Questions } from './questions';
import { getGroup, contributeAnswers } from '@/lib/quiz/groups';
import { createGroupSubmission, type GroupSubmissionState } from '@/lib/quiz/group-submission';
import GroupSubmissionRecovery from '@/components/GroupSubmissionRecovery';
import { getQuestionCatalogIdentity } from '@/lib/question-loader';
import { assertGroupResumeContext, clearGroupDraft, groupDraftSession, GROUP_DRAFT_TTL, GroupResumeError, loadGroupResumeContext, readGroupDraft, writeGroupDraft, type GroupQuizDraft, type GroupQuizScope, type GroupQuizSnapshot } from '@/lib/quiz/group-resume';
import Button from '@/components/ui/Button';

// Discriminator helper: a Phase 2 question carries a `supplementAxis` field
// (and uses ID >= 45 at runtime). Used to narrow union members without
// reaching for `as any`.
const isPhase2Question = (q: Question | Phase2Question): q is Phase2Question =>
  (q as Phase2Question).supplementAxis !== undefined || q.phase === 2;
import { useLocale } from '@/lib/locale-context';
import { t, getValueLabelTranslated } from '@/lib/translations';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  generateSessionId,
  saveQuizSession,
  getCurrentSession,
  getLatestCompletedSession,
  updateSessionAnswers,
  updateSessionSkips,
  updateSessionPhase1Results,
  addPhase2Questions,
  completeSession,
  QuizSession,
  QuizQuestion
} from '@/lib/quiz-session';
import { macroCellFromScores } from '@/lib/quiz/micro-ideology-matcher';

// Import calculateScores for live preview and DebugPanel
import { calculateScores } from './results/results-client';
import { DebugPanel } from '@/components/DebugPanel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { debugLog, debugError } from '@/lib/debug-logger';
import { saveQuizResult } from '@/lib/quiz';
import { loadGridData, findIdeologyByPosition, findDetailedIdeology, findDetailedIdeologyWithSupplementary, findDetailedIdeologyWithPredefinedMacroCell, macroCellFromSupplementKeys } from '@/lib/grid-data-loader';
import { findVisionAlignment, toVisionScale } from './results/types';

// Continuous answer value (0.0 to 1.0)
type AnswerValue = number;

// Legacy helper kept for non-UI usage; UI uses getValueLabelTranslated
const _getValueLabel = (value: number): string => {
  const percentage = Math.abs((value - 0.5) * 200);
  if (percentage <= 10) return "Neutral";

  const prefix = value < 0.5 ? "Disagree" : "Agree";
  if (percentage <= 35) return `Slightly ${prefix}`;
  if (percentage <= 65) return prefix;
  return `Strongly ${prefix}`;
};

const getPercentageDisplay = (value: number): string => {
  const percentage = Math.round(Math.abs((value - 0.5) * 200));
  return `${percentage}%`;
};


const QUESTIONS_PER_SCREEN = 12;

export default function QuizPageClient() {
  const posthog = usePostHog();
  const router = useRouter();
  const searchParams = useSearchParams();
  const quizType = searchParams.get('type') || 'short';
  const isDebugMode = searchParams.get('debug') === 'true';
  const groupId = searchParams.get('groupId');
  // Group mode reuses the short-quiz pipeline (always 12 questions, single
  // phase, no Phase 2). The only differences are: questions come from the
  // group's locked set, and on completion we also write to the group doc.
  const isGroupMode = !!groupId;
  const { locale } = useLocale();
  
  // Generate randomized questions on component mount
  const [questions, setQuestions] = useState<(Question | Phase2Question)[]>([]);
  const [, setOriginalQuestions] = useState<Question[]>([]); // Keep original for tiebreaker adjustment
  const [phase2QuestionsLoaded, setPhase2QuestionsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Calculate total screens dynamically based on current questions length
  const TOTAL_SCREENS = Math.ceil(questions.length / QUESTIONS_PER_SCREEN);
  
  const [answers, setAnswers] = useState<Record<number, AnswerValue | null>>({});
  const [skippedQuestions, setSkippedQuestions] = useState<Set<number>>(new Set());
  const [screen, setScreen] = useState(0);
  const [dragState, setDragState] = useState<{ questionId: number; isDragging: boolean } | null>(null);
  const [, setHoveredQuestion] = useState<number | null>(null);
  const [tiebreakerChecked, setTiebreakerChecked] = useState(false);
  const [liveScores, setLiveScores] = useState<{economic: number, governance: number, social: number} | null>(null);
  const [skipWarning, setSkipWarning] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const groupSubmissionRef = useRef<ReturnType<typeof createGroupSubmission> | null>(null);
  const [groupSubmissionState, setGroupSubmissionState] = useState<GroupSubmissionState | null>(null);
  const groupDraftRef = useRef<GroupQuizDraft | null>(null);
  const resumeScopeRef = useRef<GroupQuizScope | null>(null);
  const generationRef = useRef(0);
  const transitionRef = useRef(false);
  const [transitionBusy, setTransitionBusy] = useState(false);
  const [restartVersion, setRestartVersion] = useState(0);
  const [restoredProgress, setRestoredProgress] = useState(false);
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [expiredRemovalFailed, setExpiredRemovalFailed] = useState(false);
  const [resumeProblem, setResumeProblem] = useState<{ message: string; retryable: boolean; restartable?: boolean; personalResultUrl?: string } | null>(null);

  const persistGroupDraft = (draft: GroupQuizDraft) => {
    groupDraftRef.current = draft;
    if (!writeGroupDraft(draft)) setStorageUnavailable(true);
  };
  const commitGroupProgress = (patch: Partial<GroupQuizSnapshot>) => {
    const draft = groupDraftRef.current;
    if (!draft || draft.scope.groupId !== groupId || draft.scope.locale !== locale || draft.scope.quizType !== quizType) return;
    // eslint-disable-next-line react-hooks/purity -- invoked only from input/navigation handlers, never during render.
    persistGroupDraft({ ...draft, updatedAt: Date.now(), snapshot: { ...draft.snapshot, ...patch } });
  };
  const personalGroupResultUrl = (id: string) => `/quiz/results?id=${encodeURIComponent(id)}&type=${quizType}&groupId=${encodeURIComponent(groupId!)}${isDebugMode ? '&debug=true' : ''}`;
  const restartGroup = () => {
    if (!window.confirm('Start this group quiz over? Your progress in this attempt will be discarded. Any saved personal result will remain saved.')) return;
    const scope = resumeScopeRef.current;
    if (scope && !clearGroupDraft(scope)) {
      setStorageUnavailable(true);
      setResumeProblem({ message: 'This browser could not clear the saved progress. Reload recovery is unavailable. Try again when browser storage is available.', retryable: true, restartable: true });
      return;
    }
    generationRef.current++;
    groupDraftRef.current = null;
    setRestartVersion(value => value + 1);
  };
  // When taking the quiz for a compare, show who they're comparing with so the
  // motivation that brought them here carries through the quiz.
  const [compareLabel, setCompareLabel] = useState<string | null>(null);
  const answeredQuestionsRef = useRef<Set<number>>(new Set());
  const lastTrackedQuizType = useRef<string | null>(null);
  const phase2TrackedRef = useRef(false);
  const lastSkipWarningRef = useRef<string | null>(null);
  const phase2LoadingRef = useRef(false);
  // Answers carried over from a completed short quiz; drives the
  // "you're already X% done" banner on the long quiz.
  const [carriedOverCount, setCarriedOverCount] = useState(0);

  
  // Initialize randomized questions and session
  useEffect(() => {
    const generation = ++generationRef.current;
    const current = () => generationRef.current === generation;
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        groupDraftRef.current = null;
        resumeScopeRef.current = null;
        groupSubmissionRef.current = null;
        submittingRef.current = false;
        transitionRef.current = false;
        phase2LoadingRef.current = false;
        setTransitionBusy(false);
        setIsSubmitting(false);
        setGroupSubmissionState(null);
        setResumeProblem(null);
        setRestoredProgress(false);
        setStorageUnavailable(false);
        setExpiredRemovalFailed(false);
        setDragState(null);
        setSkipWarning(null);
        if (isGroupMode && groupId) {
          let verifiedUid: string | undefined;
          try {
            const context = await loadGroupResumeContext(groupId);
            verifiedUid = context.uid;
            const catalogIdentity = await getQuestionCatalogIdentity(locale);
            if (!current()) return;
            const scope: GroupQuizScope = { uid: context.uid, groupId, quizType: quizType as 'short' | 'long', locale, lockedQuestionIds: context.lockedQuestionIds, catalogIdentity };
            resumeScopeRef.current = scope;
            const stored = readGroupDraft(scope);
            const prior = 'draft' in stored ? stored.draft : undefined;
            const savedId = stored.kind === 'expired' ? stored.personalResultId : prior?.submission?.personalResultId;
            const expiryRemovalFailed = stored.kind === 'expired' && !stored.cleared;
            setExpiredRemovalFailed(expiryRemovalFailed);
            const resultUrl = savedId ? personalGroupResultUrl(savedId) : undefined;
            if (savedId && context.personalResultId === savedId && !expiryRemovalFailed) {
              clearGroupDraft(scope);
              router.replace(quizType === 'long' ? resultUrl! : `/group/${groupId}`);
              return;
            }
            try { assertGroupResumeContext(context, quizType); }
            catch (error) {
              if (!(error instanceof GroupResumeError)) throw error;
              setResumeProblem({ message: error.message, retryable: error.retryable, personalResultUrl: resultUrl });
              setIsLoading(false);
              return;
            }
            // Prepared completions are immutable. A catalog text update can
            // block editable progress while retaining an already earned result.
            const immutableRetry = stored.kind === 'incompatible' && !!savedId && !!prior?.submission?.prepared
              && JSON.stringify(prior.scope.lockedQuestionIds) === JSON.stringify(scope.lockedQuestionIds);
            if (stored.kind === 'invalid' || stored.kind === 'expired' || (stored.kind === 'incompatible' && !immutableRetry)) {
              setResumeProblem({ message: stored.kind === 'expired' ? 'Your saved quiz progress has expired. Start over to answer this group quiz.' : 'This saved progress is no longer compatible with the current questions. Start over to continue.', retryable: false, restartable: true, personalResultUrl: resultUrl });
              setIsLoading(false);
              return;
            }
            if (stored.kind === 'unavailable') setStorageUnavailable(true);
            let draft: GroupQuizDraft;
            if (stored.kind === 'found' || immutableRetry) {
              draft = prior!;
              setRestoredProgress(true);
            } else {
              const initial = quizType === 'long' ? await generateLongQuizQuestions(locale) : await generateGroupQuizQuestions(context.lockedQuestionIds, locale);
              if (!current()) return;
              if (!initial.length || (quizType === 'short' && JSON.stringify(initial.map(q => q.originalId)) !== JSON.stringify(context.lockedQuestionIds))) throw new Error('This group’s questions could not be loaded completely.');
              const now = Date.now();
              draft = { version: 1, scope, sessionId: generateSessionId(), createdAt: now, updatedAt: now, expiresAt: Math.min(now + GROUP_DRAFT_TTL, context.expiresAt), snapshot: {
                questions: initial, answers: {}, skippedQuestionIds: [], answeredQuestionIds: [], screen: 0,
                tiebreakerChecked: false, phase2QuestionsLoaded: false,
                tracked: { started: false, phase1: false, phase2: false, completed: false },
              } };
            }
            if (!current()) return;
            const snapshot = draft.snapshot;
            setQuestions(snapshot.questions);
            setOriginalQuestions([...snapshot.questions]);
            setAnswers(snapshot.answers);
            setSkippedQuestions(new Set(snapshot.skippedQuestionIds));
            setScreen(snapshot.screen);
            setTiebreakerChecked(snapshot.tiebreakerChecked);
            setPhase2QuestionsLoaded(snapshot.phase2QuestionsLoaded);
            setCarriedOverCount(0);
            answeredQuestionsRef.current = new Set(snapshot.answeredQuestionIds);
            phase2TrackedRef.current = snapshot.tracked.phase2;
            lastSkipWarningRef.current = null;
            saveQuizSession(groupDraftSession(draft));
            if (!snapshot.tracked.started) {
              draft = { ...draft, snapshot: { ...snapshot, tracked: { ...snapshot.tracked, started: true } } };
              persistGroupDraft(draft);
              capturePosthogEvent(posthog, 'quiz_session_started', { quiz_type: quizType, question_count: snapshot.questions.length, carried_over_count: 0 }, { sendToServer: true });
            } else persistGroupDraft(draft);
            lastTrackedQuizType.current = quizType;
            if (draft.submission) {
              const checkpoint = draft.submission;
              setGroupSubmissionState({ phase: 'failed', stage: savedId ? 'contribution' : 'personal_result', personalResultUrl: resultUrl,
                status: checkpoint.failure?.status, reason: checkpoint.failure?.reason,
                interrupted: !!checkpoint.prepared && !savedId });
            }
            setIsLoading(false);
          } catch (error) {
            if (!current()) return;
            const uid = verifiedUid ?? (error instanceof GroupResumeError ? error.verifiedUid : undefined);
            // A missing/unreachable group must not strand an acknowledged own
            // result. Only inspect its local pointer after fresh UID validation.
            const receipt = uid ? readGroupDraft({ uid, groupId, quizType: quizType as 'short' | 'long', locale, lockedQuestionIds: [], catalogIdentity: '' }) : undefined;
            const savedId = receipt?.kind === 'expired' ? receipt.personalResultId : receipt && 'draft' in receipt ? receipt.draft.submission?.personalResultId : undefined;
            if (receipt?.kind === 'expired') setExpiredRemovalFailed(!receipt.cleared);
            setResumeProblem({ message: error instanceof Error ? error.message : 'Could not restore this group quiz. Try again.', retryable: !(error instanceof GroupResumeError) || error.retryable,
              personalResultUrl: savedId ? personalGroupResultUrl(savedId) : undefined });
            setIsLoading(false);
          }
          return;
        }
        // A language change starts a new question set in this component.
        // Its adaptive checkpoints must run again, even if the previous
        // attempt had already reached Phase 2.
        setTiebreakerChecked(false);
        setPhase2QuestionsLoaded(false);
        
        // Solo quizzes always start fresh.
        debugLog('🧹 Starting fresh quiz session');
        if (isDebugMode) {
          debugLog('🐛 DEBUG MODE ACTIVATED - Enhanced logging enabled');
        }
        try { sessionStorage.removeItem('votely_quiz_session'); } catch { /* Storage is optional. */ }
        
        // Create new session
        debugLog(`🎯 Loading ${quizType} quiz questions (${locale})...`);
        let randomizedQuestions: Question[];
        if (quizType === 'long') {
          randomizedQuestions = await generateLongQuizQuestions(locale);
        } else {
          randomizedQuestions = await generateShortQuizQuestions(locale);
        }

        if (!current()) return;
        if (!randomizedQuestions || randomizedQuestions.length === 0) {
          throw new Error('No questions loaded from TSV file');
        }
        
        // Carry over answers from the most recent completed short quiz. The
        // 12 short questions are a strict subset of the long quiz's Phase 1
        // (11 core + 1 boundary tiebreaker), matched by originalId, so a
        // returning taker starts the long quiz ~20% done instead of
        // re-answering statements they already rated.
        const seededAnswers: Record<number, number> = {};
        const seededSkips: number[] = [];
        if (quizType === 'long' && !isGroupMode) {
          // 30-day recency bound: completed sessions live in localStorage
          // forever, and silently seeding a year-old short quiz would bake
          // outdated opinions into a fresh permanent result.
          const priorShort = getLatestCompletedSession('short', 30 * 24 * 60 * 60 * 1000);
          if (priorShort) {
            const priorSkipIds = new Set(priorShort.skippedQuestions ?? []);
            const answerByOriginalId = new Map<string, number>();
            const skippedOriginalIds = new Set<string>();
            priorShort.questions.forEach(q => {
              if (priorSkipIds.has(q.id)) {
                skippedOriginalIds.add(q.originalId);
                return;
              }
              const value = priorShort.answers[q.id];
              if (value !== undefined && value !== null) {
                answerByOriginalId.set(q.originalId, value);
              }
            });
            randomizedQuestions.forEach(q => {
              if (!q.originalId) return;
              const carried = answerByOriginalId.get(q.originalId);
              if (carried !== undefined) {
                seededAnswers[q.id] = carried;
              } else if (skippedOriginalIds.has(q.originalId)) {
                seededSkips.push(q.id);
              }
            });
          }
        }

        setScreen(0);
        setQuestions(randomizedQuestions);
        setOriginalQuestions([...randomizedQuestions]);
        answeredQuestionsRef.current.clear();
        Object.keys(seededAnswers).forEach(id => answeredQuestionsRef.current.add(Number(id)));
        setAnswers(seededAnswers);
        setSkippedQuestions(new Set(seededSkips));
        setCarriedOverCount(Object.keys(seededAnswers).length);
        phase2TrackedRef.current = false;
        lastSkipWarningRef.current = null;
        
        // Save session (only for current quiz, not for resume)
        const session: QuizSession = {
          sessionId: generateSessionId(),
          type: quizType as 'short' | 'long',
          questions: randomizedQuestions.map(q => ({
            id: q.id,
            originalId: q.originalId || `P${q.id}`,
            text: q.question,
            axis: q.axis,
            agreeDir: q.agreeDir || 1,
            phase: 1,
            qType: 'core'
          } as QuizQuestion)),
          answers: seededAnswers,
          skippedQuestions: seededSkips,
          createdAt: Date.now()
        };
        saveQuizSession(session);
        debugLog(`🎮 New quiz session created: ${session.sessionId}`);
        debugLog(`📋 ${randomizedQuestions.length} questions loaded`);
        if (lastTrackedQuizType.current !== quizType) {
          capturePosthogEvent(posthog, 'quiz_session_started', {
            quiz_type: quizType,
            question_count: randomizedQuestions.length,
            carried_over_count: Object.keys(seededAnswers).length
          }, { sendToServer: true });
          lastTrackedQuizType.current = quizType;
        }
        setIsLoading(false);
      } catch (error) {
        if (!current()) return;
        debugError('❌ Failed to load quiz questions:', error);
        setLoadError(`Failed to load quiz questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };
    void loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is an attempt-generation counter, not a DOM ref.
    return () => { if (current()) generationRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers use this initialization's scope; fresh initialization only for route/locale/restart.
  }, [quizType, posthog, locale, isGroupMode, groupId, restartVersion]);

  // Resolve who this compare is with, for the in-quiz context banner.
  useEffect(() => {
    if (!isGroupMode || !groupId) return;
    let cancelled = false;
    getGroup(groupId)
      .then((g) => {
        if (!cancelled) setCompareLabel(g?.name?.trim() || 'your friend');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isGroupMode, groupId]);

  // Load the 24 macro-cell-targeted Phase 2 questions. Called exactly once,
  // from handleNext when the user leaves the last Phase 1 screen: at that
  // point the Phase 1 answers are definitive (Back cannot cross the boundary
  // afterwards, and Phase 2 answers never touch the three core axes), so the
  // chosen macro cell always equals the cell of the final saved result. An
  // earlier version also pre-loaded eagerly from an effect the moment all 36
  // questions were answered, which let post-load slider edits on screen 2
  // flip the final cell away from the already-loaded one.
  // Group + long mode also triggers phase 2, the user gets the full long-quiz
  // result personally; the group only consumes the 12 locked answers at submit.
  const ensurePhase2Loaded = async () => {
    if (quizType !== 'long' || phase2QuestionsLoaded || phase2LoadingRef.current || questions.length !== 36) return false;
    const generation = generationRef.current;
    phase2LoadingRef.current = true;
    try {
      const loadPhase2 = async () => {
        // Same math as the final submit scoring: calculateCurrentScores
        // excludes skipped questions from the denominator and counts
        // untouched ones as neutral. This function previously had its own
        // inline calculation that divided by ALL 36 questions per axis, so a
        // skip-heavy taker near a cell boundary could get Phase 2 questions
        // from one macro cell while their saved result landed in another,
        // leaving supplementary scores keyed to a cell the results page
        // never looks up (every supplementary bar rendered as zero).
        const scores = calculateCurrentScores();

        // Single shared cell derivation: inline copies of this threshold
        // logic are exactly how the checkpoint and final cells drifted apart.
        const macroCellCode = macroCellFromScores(scores.economic, scores.governance);
        debugLog(`📍 User landed in macro cell: ${macroCellCode}`);
        debugLog(`📊 Phase 1 Scores - Economic: ${scores.economic.toFixed(2)}, Governance: ${scores.governance.toFixed(2)}, Social: ${scores.social.toFixed(2)}`);
        
        try {
          const phase2Questions = await getPhase2Questions(macroCellCode, locale);
          if (generationRef.current !== generation) return false;
          if (isGroupMode && phase2Questions.length !== 24) throw new Error('Could not load all refinement questions. Try Next again.');
          debugLog(`✨ Phase 2 Ready: ${phase2Questions.length} questions loaded`);
          
          // Add Phase 2 questions to the existing questions array
          const extendedQuestions = [...questions, ...phase2Questions];
          commitGroupProgress({ questions: extendedQuestions, phase2QuestionsLoaded: true, screen: Math.max(3, screen),
            phase1Scores: scores, macroCellCode, ...(groupDraftRef.current ? { tracked: { ...groupDraftRef.current.snapshot.tracked, phase2: true } } : {}) });
          setQuestions(extendedQuestions);
          setPhase2QuestionsLoaded(true);
          if (!phase2TrackedRef.current) {
            capturePosthogEvent(posthog, 'phase2_loaded', {
              quiz_type: quizType,
              macro_cell_code: macroCellCode,
              phase2_question_count: phase2Questions.length
            });
            phase2TrackedRef.current = true;
          }
          
          // Add Phase 2 questions to session
          const phase2SessionQuestions: QuizQuestion[] = phase2Questions.map(q => ({
            id: q.id,
            originalId: q.originalId || `P2_${q.id}`,
            text: q.question,
            axis: 'social', // Phase 2 uses supplementary axes
            agreeDir: q.agreeDir || 1,
            phase: 2,
            qType: 'refine',
            supplementAxis: q.supplementAxis
          }));
          addPhase2Questions(phase2SessionQuestions);
          
          // Update session with Phase 1 results
          updateSessionPhase1Results(scores, macroCellCode);
          
          debugLog(`🎯 Quiz extended to ${extendedQuestions.length} total questions`);
          return true;
        } catch (error) {
          debugError('Error loading Phase 2 questions:', error);
          if (isGroupMode && generationRef.current === generation) setSkipWarning('Could not load the refinement questions. Try Next again.');
          return false;
        }
      };
      
      return await loadPhase2();
    } finally {
      if (generationRef.current === generation) phase2LoadingRef.current = false;
    }
  };

  const handleAnswerSelect = (questionId: number, value: AnswerValue | null) => {
    // DEBUG: Log answer changes
    if (value === null) {
      debugLog(`🔄 handleAnswerSelect Q${questionId}: Setting to NULL (skip)`);
    } else if (answers[questionId] === null || answers[questionId] === undefined) {
      debugLog(`🔄 handleAnswerSelect Q${questionId}: ${answers[questionId]} → ${value} (answering after skip)`);
    }

    const newAnswers = {
      ...(isGroupMode ? groupDraftRef.current?.snapshot.answers ?? answers : answers),
      [questionId]: value
    };

    // DEBUG: Verify the newAnswers object
    debugLog(`🔄 newAnswers[${questionId}] = ${newAnswers[questionId]} (type: ${typeof newAnswers[questionId]})`);

    setAnswers(newAnswers);

    // DEBUG: Log what we're updating in session
    debugLog(`🔄 Updating session with: { ${questionId}: ${value} }`);

    if (value !== null && !answeredQuestionsRef.current.has(questionId)) {
      // Track first-time answer locally so phase1_completed / quiz_completed
      // can ship aggregate counts. Per-question events were retired because
      // they accounted for ~41% of our PostHog volume and were never used
      // by any insight (see /loop event-volume audit, 2026-06).
      answeredQuestionsRef.current.add(questionId);
    }

    // Remove from skipped set if answering after skip
    if (value !== null && skippedQuestions.has(questionId)) {
      const newSkipped = new Set(skippedQuestions);
      newSkipped.delete(questionId);
      setSkippedQuestions(newSkipped);
      updateSessionSkips([...newSkipped]);
      debugLog(`🔄 Removed Q${questionId} from skipped set - answering after skip`);

    }

    const persistedSkips = new Set(groupDraftRef.current?.snapshot.skippedQuestionIds ?? skippedQuestions);
    if (value !== null) persistedSkips.delete(questionId);
    commitGroupProgress({ answers: newAnswers, skippedQuestionIds: [...persistedSkips], answeredQuestionIds: [...answeredQuestionsRef.current] });
    // Update session
    updateSessionAnswers({ [questionId]: value });
  };

  const handleSkipQuestion = (questionId: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    // Check if we can skip this question (enforce 50% per-axis limit)
    const newSkipped = new Set(skippedQuestions);
    newSkipped.add(questionId);

    // Count skips by axis (including supplementary axes for Phase 2)
    const skipsByAxis: Record<string, number> = {};
    const questionsByAxis: Record<string, number> = {};

    // Initialize counts for all axes
    questions.forEach(q => {
      if (q.id < 45) {
        // Phase 1: main axes (including tiebreakers)
        if (q.axis) {
          if (!skipsByAxis[q.axis]) skipsByAxis[q.axis] = 0;
          if (!questionsByAxis[q.axis]) questionsByAxis[q.axis] = 0;
          questionsByAxis[q.axis]++;
        }
      } else if (isPhase2Question(q)) {
        // Phase 2: supplementary axes
        const suppAxis = q.supplementAxis;
        if (suppAxis) {
          if (!skipsByAxis[suppAxis]) skipsByAxis[suppAxis] = 0;
          if (!questionsByAxis[suppAxis]) questionsByAxis[suppAxis] = 0;
          questionsByAxis[suppAxis]++;
        }
      }
    });

    // Debug: Log the current question distribution
    debugLog(`📊 Current question distribution:`, questionsByAxis);

    // Count current skips
    newSkipped.forEach(qId => {
      const q = questions.find(qu => qu.id === qId);
      if (q) {
        if (q.id < 45) {
          if (q.axis) {
            if (!skipsByAxis[q.axis]) skipsByAxis[q.axis] = 0;
            skipsByAxis[q.axis]++;
          }
        } else if (isPhase2Question(q)) {
          const suppAxis = q.supplementAxis;
          if (suppAxis) {
            if (!skipsByAxis[suppAxis]) skipsByAxis[suppAxis] = 0;
            skipsByAxis[suppAxis]++;
          }
        }
      }
    });

    // Determine which axis this question belongs to
    const targetAxis = question.id < 45 ? question.axis : (isPhase2Question(question) ? question.supplementAxis : undefined);

    if (targetAxis && questionsByAxis[targetAxis]) {
      const currentSkips = skipsByAxis[targetAxis] || 0;
      const totalQuestions = questionsByAxis[targetAxis];
      const wouldBeSkipPercentage = (currentSkips / totalQuestions) * 100;

      // Debug the skip calculation
      debugLog(`🔍 Skip check for Q${questionId} (${targetAxis}):`);
      debugLog(`  Current skips in ${targetAxis}: ${currentSkips}`);
      debugLog(`  Total questions in ${targetAxis}: ${totalQuestions}`);
      debugLog(`  Would be skip percentage: ${wouldBeSkipPercentage.toFixed(1)}%`);
      debugLog(`  All questions in axis:`, questions.filter(q => q.id < 45 && q.axis === targetAxis).map(q => `Q${q.id}`));

      if (wouldBeSkipPercentage > 50) {
        // Show error as a temporary toast
        setSkipWarning(`Cannot skip: You've already skipped 50% of ${targetAxis} questions (${currentSkips}/${totalQuestions}). This would reduce accuracy too much.`);
        // Auto-hide after 3 seconds
        setTimeout(() => setSkipWarning(null), 3000);
        return; // Don't allow the skip
      }
    } else {
      debugLog(`⚠️ Skip check failed - targetAxis: ${targetAxis}, questionsByAxis[${targetAxis}]: ${targetAxis ? questionsByAxis[targetAxis] : undefined}`);
    }

    commitGroupProgress({ skippedQuestionIds: [...newSkipped] });
    // Allow the skip - only update skippedQuestions Set, don't modify answers.
    // Per-skip PostHog events were retired alongside question_answered;
    // skippedQuestions.size is shipped on phase1_completed and quiz_completed.
    setSkippedQuestions(newSkipped);
    // Persist to the session so a completed short quiz carries its skips
    // into the long quiz as skips, not as scored neutrals.
    updateSessionSkips([...newSkipped]);

    // Recalculate scores immediately with the new skipped set
    const recalculatedScores = calculateCurrentScores(true, newSkipped);
    setLiveScores(recalculatedScores);
    debugLog(`🔄 Recalculated scores after skip - Economic: ${recalculatedScores.economic.toFixed(2)}, Governance: ${recalculatedScores.governance.toFixed(2)}, Social: ${recalculatedScores.social.toFixed(2)}`);

    // Only recalculate Phase 2 supplementary scores if this axis was already fully answered
    if (questionId >= 45) {
      const skippedQuestion = questions.find(q => q.id === questionId);
      if (skippedQuestion && isPhase2Question(skippedQuestion) && skippedQuestion.supplementAxis) {
        const suppAxis = skippedQuestion.supplementAxis;

        // First, check if ALL questions for this axis were previously answered
        const axisQuestions = questions.filter((q): q is Phase2Question =>
          q.id >= 45 && isPhase2Question(q) && q.supplementAxis === suppAxis
        );
        const answeredBeforeSkip = axisQuestions.filter(q =>
          answers[q.id] !== undefined && answers[q.id] !== null && !skippedQuestions.has(q.id)
        );

        // Only recalculate if this axis was fully answered before the skip
        if (answeredBeforeSkip.length === axisQuestions.length) {
          debugLog(`🔄 ${suppAxis} was fully answered (${axisQuestions.length}/${axisQuestions.length}), recalculating after skip Q${questionId}...`);

          // Calculate supplementary scores for this axis (excluding the newly skipped question)
          const supplementaryScores: Record<string, { score: number; count: number }> = {};
          axisQuestions.forEach(q => {
            if (!newSkipped.has(q.id)) {
              const answer = answers[q.id];
              if (answer !== undefined && answer !== null) {
                if (!supplementaryScores[suppAxis]) {
                  supplementaryScores[suppAxis] = { score: 0, count: 0 };
                }
                const score = (answer - 0.5) * 4;
                const contribution = (q.agreeDir || 1) * score;
                supplementaryScores[suppAxis].score += contribution;
                supplementaryScores[suppAxis].count++;
              }
            }
          });

          if (supplementaryScores[suppAxis]) {
            const maxScore = supplementaryScores[suppAxis].count * 2;
            const normalizedScore = supplementaryScores[suppAxis].count > 0
              ? (supplementaryScores[suppAxis].score + maxScore) / (maxScore * 2) * 100
              : 0;
            debugLog(`🔄 ${suppAxis} after skip: ${normalizedScore.toFixed(1)}% (Raw: ${supplementaryScores[suppAxis].score.toFixed(2)}/${maxScore}, ${supplementaryScores[suppAxis].count} questions)`);
          }
        } else {
          debugLog(`⏩ ${suppAxis} not fully answered yet (${answeredBeforeSkip.length}/${axisQuestions.length}), skipping recalculation to avoid locking incomplete state`);
        }
      }
    }

    // DEBUG: Log the skip action
    debugLog(`⏭️ SKIPPING Q${questionId}: Adding to skipped set (keeping answer: ${answers[questionId]})`);
    debugLog(`   Question added to skip set: ${newSkipped.has(questionId)}`);

    // Update warnings
    checkSkipLimits(newSkipped);
  };

  const checkSkipLimits = (skipped: Set<number>) => {
    // Count skips by axis (including supplementary axes)
    const skipsByAxis: Record<string, number> = {};
    const questionsByAxis: Record<string, number> = {};

    // Initialize counts for all axes
    questions.forEach(q => {
      if (q.id < 45) {
        // Phase 1: main axes (including tiebreakers)
        if (q.axis) {
          if (!questionsByAxis[q.axis]) questionsByAxis[q.axis] = 0;
          questionsByAxis[q.axis]++;
          // Debug tiebreaker axis detection
          if (q.boundary) {
            debugLog(`  🔍 Tiebreaker Q${q.id} has axis: ${q.axis}, boundary: ${q.boundary}`);
          }
        } else {
          debugLog(`  ⚠️ Phase 1 Question Q${q.id} missing axis property`);
        }
      } else if (isPhase2Question(q)) {
        // Phase 2: supplementary axes
        const suppAxis = q.supplementAxis;
        if (suppAxis) {
          if (!questionsByAxis[suppAxis]) questionsByAxis[suppAxis] = 0;
          questionsByAxis[suppAxis]++;
        }
      }
    });

    // Count current skips
    skipped.forEach(qId => {
      const question = questions.find(q => q.id === qId);
      if (question) {
        if (question.id < 45) {
          if (question.axis) {
            if (!skipsByAxis[question.axis]) skipsByAxis[question.axis] = 0;
            skipsByAxis[question.axis]++;
          }
        } else if (isPhase2Question(question)) {
          const suppAxis = question.supplementAxis;
          if (suppAxis) {
            if (!skipsByAxis[suppAxis]) skipsByAxis[suppAxis] = 0;
            skipsByAxis[suppAxis]++;
          }
        }
      }
    });

    // Calculate overall skip percentage for Phase 1
    const totalPhase1Questions = questions.filter(q => q.id < 45).length;
    const phase1Skips = Array.from(skipped).filter(qId => qId < 45).length;
    const skipPercentage = totalPhase1Questions > 0 ? (phase1Skips / totalPhase1Questions) * 100 : 0;

    // Check for warnings
    const warnings = [];

    // Overall skip percentage warning
    if (skipPercentage >= 25) {
      warnings.push(`You've skipped ${Math.round(skipPercentage)}% of questions.`);
    }

    // Axis-specific warnings (approaching 50% limit)
    const axisApproachingLimit: string[] = [];
    Object.entries(skipsByAxis).forEach(([axis, count]) => {
      if (questionsByAxis[axis] && questionsByAxis[axis] > 0) {
        const axisSkipPercentage = (count / questionsByAxis[axis]) * 100;
        if (axisSkipPercentage >= 40) { // Warning when close to 50% limit
          axisApproachingLimit.push(`${axis} (${Math.round(axisSkipPercentage)}%)`);
        }
      }
    });

    if (axisApproachingLimit.length > 0) {
      warnings.push(`Approaching skip limit for: ${axisApproachingLimit.join(', ')}`);
    }

    if (warnings.length > 0) {
      const warningMessage = warnings.join(' ');
      setSkipWarning(warningMessage);
      // Auto-hide info warnings after 5 seconds
      setTimeout(() => setSkipWarning(null), 5000);
      if (lastSkipWarningRef.current !== warningMessage) {
        lastSkipWarningRef.current = warningMessage;
      }
    } else {
      setSkipWarning(null);
      lastSkipWarningRef.current = null;
    }
  };

  // The invisible hit area is deliberately larger than the track (px-10
  // -mx-10 plus pt-8/pb-8) so the slider is easy to grab. On a 390px phone
  // that makes it span x -8..398 against a track of 32..358, so a press in
  // either 40px overhang used to clamp straight to 0 or 1 and silently record
  // "Strongly Disagree"/"Strongly Agree". Prod data showed the fingerprint:
  // rows answering exactly 1 or 0 on a single question with the other eleven
  // untouched. A press has to land on the track (plus a small grab margin) to
  // count; once a drag is under way, moving past the end still clamps, which
  // is what you want.
  const GRAB_MARGIN_PX = 12;
  // Touch gestures (Sept 2026 tester report: scrolling down the page kept
  // rewriting earlier answers). The hit area used to be `touch-action: none`
  // and wrote the answer on touchstart, so every scroll that began on one of
  // the twelve 64px-tall slider strips was swallowed and turned into a drag.
  // Now the strip is `touch-action: pan-y`: the browser owns vertical
  // scrolling (and fires pointercancel when it takes a gesture over), and a
  // touch only becomes an answer when it is clearly one:
  //   - a tap (under TOUCH_SLOP_PX of movement) sets an UNANSWERED slider on
  //     release, so the first answer is still one tap anywhere on the track;
  //   - a mostly horizontal move past TOUCH_SLOP_PX starts a drag;
  //   - an ANSWERED slider only moves when the drag starts on its thumb
  //     (like a native iOS slider), so stray taps and sloppy diagonal swipes
  //     over old answers cannot change them. A tap on its track away from
  //     the thumb bounces the thumb instead, to show where to grab;
  //   - a tap that lands while the page is still coasting from a flick is
  //     a "stop scrolling" tap and is ignored.
  // Mouse input keeps the original behaviour: press anywhere on the track
  // to jump there, then drag.
  // At or above the browsers' own touch slop (about 8px in Chrome), so the
  // browser has already claimed a vertical scroll (pointercancel) before a
  // move ever reaches this threshold.
  const TOUCH_SLOP_PX = 12;
  const THUMB_GRAB_RADIUS_PX = 28;
  const SCROLL_SETTLE_MS = 150;

  const sliderGestureRef = useRef<{
    pointerId: number;
    questionId: number;
    element: HTMLElement;
    startX: number;
    startY: number;
    mode: 'pending' | 'dragging';
    answered: boolean;
    startAnswer: number | undefined;
    onThumb: boolean;
    // Finger distance from the thumb centre when grabbing an answered
    // thumb, so the thumb follows the finger without jumping under it.
    grabOffsetPx: number;
    duringScroll: boolean;
  } | null>(null);
  const lastScrollAtRef = useRef(-Infinity);
  const [nudgedQuestion, setNudgedQuestion] = useState<number | null>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = (event: Event) => { lastScrollAtRef.current = event.timeStamp; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, []);

  // Maps a clientX to a 0..1 answer on this hit area's track. With `strict`,
  // returns null when the point is outside the track plus GRAB_MARGIN_PX.
  const sliderValueAt = (element: HTMLElement, clientX: number, strict: boolean): number | null => {
    const sliderTrack = element.querySelector('.slider-track');
    if (!sliderTrack) return null;
    const rect = sliderTrack.getBoundingClientRect();
    if (rect.width === 0) return null; // not laid out yet; would yield NaN
    const relativeX = clientX - rect.left;
    if (strict && (relativeX < -GRAB_MARGIN_PX || relativeX > rect.width + GRAB_MARGIN_PX)) return null;
    return Math.max(0, Math.min(1, relativeX / rect.width));
  };

  const endSliderGesture = () => {
    sliderGestureRef.current = null;
    setDragState(null);
  };

  const startSliderDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = sliderGestureRef.current;
    if (!gesture) return;
    gesture.mode = 'dragging';
    try { gesture.element.setPointerCapture(gesture.pointerId); } catch { /* pointer already gone */ }
    setDragState({ questionId: gesture.questionId, isDragging: true });
    const value = sliderValueAt(gesture.element, event.clientX - gesture.grabOffsetPx, false);
    if (value !== null) handleAnswerSelect(gesture.questionId, value);
  };

  const nudgeThumb = (questionId: number) => {
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    // Clear first so a second tap restarts the animation.
    setNudgedQuestion(null);
    requestAnimationFrame(() => setNudgedQuestion(questionId));
    nudgeTimerRef.current = setTimeout(() => setNudgedQuestion(null), 600);
  };

  const handleSliderPointerDown = (questionId: number, event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const element = event.currentTarget;
    const startValue = sliderValueAt(element, event.clientX, true);
    if (startValue === null) return;
    event.stopPropagation();

    const existing = answers[questionId];
    const answered = existing !== undefined && existing !== null;
    sliderGestureRef.current = {
      pointerId: event.pointerId,
      questionId,
      element,
      startX: event.clientX,
      startY: event.clientY,
      mode: 'pending',
      answered,
      startAnswer: answered ? existing : undefined,
      onThumb: false,
      grabOffsetPx: 0,
      // Event timestamps share one clock, so this compares like with like.
      duringScroll: event.timeStamp - lastScrollAtRef.current < SCROLL_SETTLE_MS,
    };

    if (event.pointerType === 'mouse') {
      event.preventDefault(); // no text selection while dragging
      startSliderDrag(event); // jumps to the pressed point, then follows
      return;
    }

    if (answered) {
      const rect = element.querySelector('.slider-track')!.getBoundingClientRect();
      const thumbX = rect.left + rect.width * existing;
      const offset = event.clientX - thumbX;
      if (Math.abs(offset) <= THUMB_GRAB_RADIUS_PX) {
        sliderGestureRef.current.onThumb = true;
        sliderGestureRef.current.grabOffsetPx = offset;
      }
    }
  };

  const handleSliderPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = sliderGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    if (gesture.mode === 'dragging') {
      const value = sliderValueAt(gesture.element, event.clientX - gesture.grabOffsetPx, false);
      if (value !== null) handleAnswerSelect(gesture.questionId, value);
      return;
    }

    const dx = Math.abs(event.clientX - gesture.startX);
    const dy = Math.abs(event.clientY - gesture.startY);
    if (dx < TOUCH_SLOP_PX && dy < TOUCH_SLOP_PX) return;
    if (dx > dy && (!gesture.answered || gesture.onThumb)) {
      startSliderDrag(event);
    } else {
      // A scroll (or a sideways brush across an answered track): let it go.
      sliderGestureRef.current = null;
    }
  };

  const handleSliderPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = sliderGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (gesture.mode === 'pending' && !gesture.duringScroll) {
      if (!gesture.answered) {
        const value = sliderValueAt(gesture.element, gesture.startX, true);
        if (value !== null) handleAnswerSelect(gesture.questionId, value);
      } else if (!gesture.onThumb) {
        nudgeThumb(gesture.questionId);
      }
    }
    endSliderGesture();
  };

  const handleSliderPointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = sliderGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    // The browser took the gesture over (usually as a scroll) after a drag
    // had begun: put an existing answer back where it was.
    if (gesture.mode === 'dragging' && event.pointerType !== 'mouse' && gesture.startAnswer !== undefined) {
      handleAnswerSelect(gesture.questionId, gesture.startAnswer);
    }
    endSliderGesture();
  };

  // Calculate current scores for the tiebreaker and Phase 2 checkpoints.
  // `upTo` bounds scoring to the questions the user has actually been shown:
  // untouched-but-shown questions count as neutral (0 contribution, in the
  // denominator, matching the final submit's `?? 0.5` coercion), but
  // never-shown questions must be excluded entirely or they dilute the
  // scores. The screen-1 tiebreaker checkpoint passes 24 (screens 1-2);
  // everything after Phase 1 uses the full 36.
  const calculateCurrentScores = (debug: boolean = false, skippedSet?: Set<number>, upTo: number = 36) => {
    const currentSkipped = skippedSet || skippedQuestions;
    let economicScore = 0;
    let governanceScore = 0; // Renamed from socialScore for clarity
    let socialScore = 0;
    let economicQuestions = 0;
    let governanceQuestions = 0; // Renamed from socialQuestions
    let socialQuestions = 0;
    
    // Convert continuous values (0-1) to score values (-2 to +2)
    const convertToScore = (value: number): number => {
      return (value - 0.5) * 4; // Maps 0->-2, 0.5->0, 1->2
    };
    
    // Only calculate for Phase 1 questions, bounded by how far the user has
    // actually gotten (see `upTo` above).
    const questionsToScore = questions.slice(0, Math.min(questions.length, upTo));

    if (debug) {
      debugLog('🔍 DETAILED SCORING BREAKDOWN:');
      debugLog('================================');

      // Count skipped vs answered questions for tiebreaker context
      const skipCounts = { economic: 0, governance: 0, social: 0 };
      const answerCounts = { economic: 0, governance: 0, social: 0 };

      questionsToScore.forEach(q => {
        const answer = answers[q.id];
        if (answer === undefined || answer === null || currentSkipped.has(q.id)) {
          if (q.axis) skipCounts[q.axis]++;
        } else {
          if (q.axis) answerCounts[q.axis]++;
        }
      });

      debugLog(`📊 Question Status (for tiebreaker calculation):`);
      debugLog(`   Economic: ${answerCounts.economic} answered, ${skipCounts.economic} skipped`);
      debugLog(`   Governance: ${answerCounts.governance} answered, ${skipCounts.governance} skipped`);
      debugLog(`   Social: ${answerCounts.social} answered, ${skipCounts.social} skipped`);
      debugLog('================================');
    }

    questionsToScore.forEach((question) => {
      // Skipped questions are excluded from numerator AND denominator,
      // matching the final submit scoring (handleSubmit filters them out of
      // questionData). Untouched questions count as neutral: 0 contribution
      // but still in the denominator, matching the final `?? 0.5` coercion.
      // Keeping this identical to the final scoring is what guarantees the
      // Phase 2 macro cell equals the cell of the saved result.
      if (currentSkipped.has(question.id)) return;
      const answer = answers[question.id];
      if (answer === undefined || answer === null) {
        if (question.axis === 'economic') economicQuestions++;
        else if (question.axis === 'governance') governanceQuestions++;
        else if (question.axis === 'social') socialQuestions++;
        return;
      }

      const score = convertToScore(answer);
      const agreeDir = question.agreeDir || 1;
      const contribution = score * agreeDir;
      
      if (question.axis === 'economic') {
        // agreeDir -1 means agreeing pushes left (negative), 1 means agreeing pushes right (positive)
        economicScore += contribution;
        economicQuestions++;
        if (debug) {
          const direction = agreeDir === -1 ? 'LEFT' : 'RIGHT';
          debugLog(`Q${question.id} (Econ-${direction}): Answer=${answer.toFixed(2)} → Score=${score.toFixed(2)} × Dir=${agreeDir} = ${contribution.toFixed(2)}`);
        }
      } else if (question.axis === 'governance') {
        // agreeDir -1 means agreeing pushes libertarian (negative), 1 means agreeing pushes authoritarian (positive)
        governanceScore += contribution;
        governanceQuestions++;
        if (debug) {
          const direction = agreeDir === -1 ? 'LIB' : 'AUTH';
          debugLog(`Q${question.id} (Auth-${direction}): Answer=${answer.toFixed(2)} → Score=${score.toFixed(2)} × Dir=${agreeDir} = ${contribution.toFixed(2)}`);
        }
      } else if (question.axis === 'social') {
        // agreeDir -1 means agreeing pushes progressive (negative), 1 means agreeing pushes traditional (positive)
        socialScore += contribution;
        socialQuestions++;
        if (debug) {
          const direction = agreeDir === -1 ? 'PROG' : 'TRAD';
          debugLog(`Q${question.id} (Social-${direction}): Answer=${answer.toFixed(2)} → Score=${score.toFixed(2)} × Dir=${agreeDir} = ${contribution.toFixed(2)}`);
        }
      }
    });
    
    if (debug) {
      debugLog('================================');
      debugLog(`Economic: Raw sum=${economicScore.toFixed(2)}, Questions=${economicQuestions}, Max possible=±${economicQuestions * 2}`);
      debugLog(`Authority: Raw sum=${governanceScore.toFixed(2)}, Questions=${governanceQuestions}, Max possible=±${governanceQuestions * 2}`);
      debugLog(`Social: Raw sum=${socialScore.toFixed(2)}, Questions=${socialQuestions}, Max possible=±${socialQuestions * 2}`);
    }
    
    // Normalize to -100 to +100 scale
    const maxEconomicScore = economicQuestions * 2;
    const maxGovernanceScore = governanceQuestions * 2;
    const maxSocialScore = socialQuestions * 2;
    
    const normalizedEconomic = maxEconomicScore > 0 ? (economicScore / maxEconomicScore) * 100 : 0;
    const normalizedGovernance = maxGovernanceScore > 0 ? (governanceScore / maxGovernanceScore) * 100 : 0;
    const normalizedSocial = maxSocialScore > 0 ? (socialScore / maxSocialScore) * 100 : 0;
    
    if (debug) {
      debugLog(`Normalized Economic: ${normalizedEconomic.toFixed(2)}% (${normalizedEconomic < 0 ? 'LEFT' : 'RIGHT'})`);
      debugLog(`Normalized Authority: ${normalizedGovernance.toFixed(2)}% (${normalizedGovernance < 0 ? 'LIBERTARIAN' : 'AUTHORITARIAN'})`);
      debugLog(`Normalized Social: ${normalizedSocial.toFixed(2)}% (${normalizedSocial < 0 ? 'PROGRESSIVE' : 'TRADITIONAL'})`);
      debugLog('================================');
    }
    
    return { economic: normalizedEconomic, governance: normalizedGovernance, social: normalizedSocial };
  };

  const handleNext = async () => {
    if (isGroupMode && transitionRef.current) return;
    const generation = generationRef.current;
    if (isGroupMode) { transitionRef.current = true; setTransitionBusy(true); }
    let nextQuestions = questions;
    let nextSkipped = skippedQuestions;
    let checked = tiebreakerChecked;
    try {
      debugLog(`🎮 Screen transition: ${screen} -> ${screen + 1}, Total screens: ${TOTAL_SCREENS}, Questions: ${questions.length}`);
    
      // Get current screen questions for Phase 2 check
      // screen_advanced was retired in the 2026-06 telemetry trim — it fired
      // ~5 times per quiz session (every 12 questions) and no insight read
      // from it. phase1_completed already carries the aggregate counts.

      // Check for tiebreakers after screen 1 (24 questions answered)
      // This will potentially modify questions 25-36 on screen 2
      if (screen === 1 && quizType === 'long' && !tiebreakerChecked && questions.length === 36) {
        debugLog('🔍 === CHECKPOINT: 24 QUESTIONS COMPLETED ===');

        // Score ONLY the 24 questions the user has been shown (screens 1-2).
        // Questions 25-36 have never been rendered; letting them into the
        // denominator as neutrals deflates every axis by ~1/3, and the
        // tiebreaker zone constants (+/-22..+/-56) were tuned for scores over
        // the answered questions.
        const scores = calculateCurrentScores(false, undefined, 24);
        debugLog(`📊 Scores after 24 questions - Economic: ${scores.economic.toFixed(2)}, Authority: ${scores.governance.toFixed(2)}, Social: ${scores.social.toFixed(2)}`);
      
        // Log current session data for debugging
        const currentSession = getCurrentSession();
        if (currentSession) {
          debugLog('📋 Session status:', {
            sessionId: currentSession.sessionId,
            questionsAnswered: Object.keys(currentSession.answers).length,
            totalQuestions: questions.length
          });
        }
      
        // Detect which tiebreaker zones we're in
        // New threshold system: -56 to -22 and +22 to +56 (macro boundaries remain ±33.33)
        const boundaries: string[] = [];
      
        if (scores.economic >= -56 && scores.economic <= -22) {
          boundaries.push('LEFT_CENTER');
          debugLog(`📍 In LEFT_CENTER tiebreaker zone (economic: ${scores.economic.toFixed(2)} in range -56 to -22)`);
        }
        if (scores.economic >= 22 && scores.economic <= 56) {
          boundaries.push('CENTER_RIGHT');
          debugLog(`📍 In CENTER_RIGHT tiebreaker zone (economic: ${scores.economic.toFixed(2)} in range +22 to +56)`);
        }
        if (scores.governance >= -56 && scores.governance <= -22) {
          boundaries.push('LIB_CENTER');
          debugLog(`📍 In LIB_CENTER tiebreaker zone (authority: ${scores.governance.toFixed(2)} in range -56 to -22)`);
        }
        if (scores.governance >= 22 && scores.governance <= 56) {
          boundaries.push('CENTER_AUTH');
          debugLog(`📍 In CENTER_AUTH tiebreaker zone (authority: ${scores.governance.toFixed(2)} in range +22 to +56)`);
        }
      
        // Get tiebreaker questions if needed
        if (boundaries.length > 0) {
          debugLog(`⚖️ TIEBREAKERS NEEDED! Near ${boundaries.length} boundary(ies): ${boundaries.join(', ')}`);
          const tiebreakerQuestions = await getTiebreakerQuestionsAsync(boundaries, locale);
          if (generationRef.current !== generation) return;
          if (isGroupMode && !tiebreakerQuestions.length) throw new Error('Could not load the next questions. Try Next again.');
          debugLog(`📌 Loading ${tiebreakerQuestions.length} tiebreaker questions to clarify position`);
          capturePosthogEvent(posthog, 'tiebreaker_loaded', {
            quiz_type: quizType,
            boundaries,
            tiebreaker_count: tiebreakerQuestions.length
          });
        
          // Integrate tiebreaker questions into questions 25-36 based on removal priority
          const questionsToModify = [...questions.slice(24, 36)]; // Questions 25-36 (make a copy)
          const questionsBeforeModification = questions.slice(0, 24); // Questions 1-24
        
          // Categorize social questions by removal priority
          const socialQuestionsByPriority: { index: number; question: Question | Phase2Question; priority: string }[] = [];
          questionsToModify.forEach((q, index) => {
            if (q.axis === 'social') {
              socialQuestionsByPriority.push({
                index,
                question: q,
                priority: q.removalPriority || 'none'
              });
            }
          });

          // Sort by removal priority: 'first' comes first, then 'second', then others
          socialQuestionsByPriority.sort((a, b) => {
            const priorityOrder: Record<string, number> = { 'first': 0, 'second': 1, 'none': 2 };
            return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
          });

          debugLog(`📊 Questions 25-36 social questions by removal priority:`);

          // Debug: Log actual removalPriority values
          socialQuestionsByPriority.forEach((item) => {
            debugLog(`    Q${25 + item.index}: removalPriority="${item.question.removalPriority}" → priority="${item.priority}"`);
          });
        
          const firstPriority = socialQuestionsByPriority.filter(q => q.priority === 'first');
          const secondPriority = socialQuestionsByPriority.filter(q => q.priority === 'second');
          const noPriority = socialQuestionsByPriority.filter(q => q.priority === 'none');
          debugLog(`  - 'first' priority (remove if 1+ boundaries): ${firstPriority.length} questions`);
          debugLog(`  - 'second' priority (remove if 2+ boundaries): ${secondPriority.length} questions`);
          debugLog(`  - no removal priority: ${noPriority.length} questions`);
        
          // Debug: Show which specific questions have which priorities
          if (firstPriority.length > 0) {
            debugLog(`    First priority questions: ${firstPriority.map(q => q.question.originalId || q.question.id).join(', ')}`);
          }
          if (secondPriority.length > 0) {
            debugLog(`    Second priority questions: ${secondPriority.map(q => q.question.originalId || q.question.id).join(', ')}`);
          }
        
          // Determine which questions to replace based on number of boundaries
          const questionsToReplace: typeof socialQuestionsByPriority = [];
        
          if (boundaries.length >= 1) {
            // Remove all 'first' priority questions
            questionsToReplace.push(...firstPriority);
            debugLog(`  → Removing ${firstPriority.length} 'first' priority questions (${boundaries.length} boundary detected)`);
          }
        
          if (boundaries.length >= 2) {
            // Also remove 'second' priority questions
            questionsToReplace.push(...secondPriority);
            debugLog(`  → Also removing ${secondPriority.length} 'second' priority questions (${boundaries.length} boundaries detected)`);
          }

          // Replace the questions
          const numReplacements = Math.min(tiebreakerQuestions.length, questionsToReplace.length);

          if (numReplacements > 0) {
            // Track which questions are being replaced for skip handling
            const replacedQuestionIds = new Set<number>();

            for (let i = 0; i < numReplacements; i++) {
              const replaceItem = questionsToReplace[i];
              const tiebreaker = tiebreakerQuestions[i];
              const oldQuestionId = questionsToModify[replaceItem.index].id;

              debugLog(`  🔄 Replacing Q${25 + replaceItem.index}: "${replaceItem.question.question.substring(0, 50)}..." → "${tiebreaker.question.substring(0, 50)}..."`);
              debugLog(`  📊 Tiebreaker axis info: axis="${tiebreaker.axis}", boundary="${tiebreaker.boundary}"`);
              questionsToModify[replaceItem.index] = tiebreaker;
              replacedQuestionIds.add(oldQuestionId);
            }

            // Update the full questions array
            const updatedQuestions = [...questionsBeforeModification, ...questionsToModify];
            nextQuestions = updatedQuestions;
            setQuestions(updatedQuestions);

            // Clean up skipped questions that were replaced by tiebreakers
            if (replacedQuestionIds.size > 0) {
              const updatedSkipped = new Set(skippedQuestions);
              replacedQuestionIds.forEach(id => {
                if (updatedSkipped.has(id)) {
                  updatedSkipped.delete(id);
                  debugLog(`  🧹 Removed skip for replaced question ID ${id}`);
                }
              });
              nextSkipped = updatedSkipped;
              setSkippedQuestions(updatedSkipped);
              updateSessionSkips([...updatedSkipped]);

              // Recalculate skip limits with new questions
              checkSkipLimits(updatedSkipped);
            }

            debugLog(`✅ Integrated ${numReplacements} tiebreaker questions based on removal priorities`);
            debugLog(`📝 Modified quiz maintains 36 Phase 1 questions with better boundary resolution`);
          } else {
            debugLog('⚠️ No questions with appropriate removal priority to replace');
          }
        } else {
          debugLog('✅ No tiebreakers needed - scores are clear of boundaries');
          debugLog('📝 Continuing with standard questions 25-36');
        }
      
        // Mark that we've checked for tiebreakers
        checked = true;
        setTiebreakerChecked(true);
      }
    
      // Log checkpoint after all 36 Phase 1 questions are complete (moving from screen 2)
      if (screen === 2 && quizType === 'long' && questions.length >= 36 && !phase2QuestionsLoaded) {
        debugLog('🎯 === CHECKPOINT: ALL 36 PHASE 1 QUESTIONS COMPLETED ===');
        const scores = calculateCurrentScores();
        debugLog(`📊 Final Phase 1 Scores - Economic: ${scores.economic.toFixed(2)}, Authority: ${scores.governance.toFixed(2)}, Social: ${scores.social.toFixed(2)}`);

        const macroCellCode = macroCellFromScores(scores.economic, scores.governance);
        debugLog(`📍 Macro cell determined: ${macroCellCode}`);
        debugLog('⏳ Phase 2 questions will load automatically via useEffect...');
        if (!groupDraftRef.current?.snapshot.tracked.phase1) {
          if (groupDraftRef.current) commitGroupProgress({ tracked: { ...groupDraftRef.current.snapshot.tracked, phase1: true } });
          capturePosthogEvent(posthog, 'phase1_completed', {
            quiz_type: quizType,
            macro_cell_code: macroCellCode,
            economic_score: scores.economic,
            governance_score: scores.governance,
            social_score: scores.social,
            // Aggregate counts replace the per-question events that previously
            // ran ~60x per long-quiz session. Funnel analyses can still split
            // by answered vs skipped without paying for 60 events per user.
            questions_answered: answeredQuestionsRef.current.size,
            questions_skipped: skippedQuestions.size,
          }, { sendToServer: true });
        }
        // Next is never disabled anymore, so Phase 1 can end with untouched
        // (implicitly neutral) questions and the completeness effect may not
        // have fired. Load Phase 2 now, before advancing to screen 3. The TSV
        // is already cached client-side, so this await is near-instant.
        const loaded = await ensurePhase2Loaded();
        if (isGroupMode && !loaded) return;
      }
      if (generationRef.current !== generation) return;
      if (isGroupMode && !(screen === 2 && quizType === 'long')) {
        const activeIds = new Set(nextQuestions.map(q => q.id));
        commitGroupProgress({ questions: nextQuestions, skippedQuestionIds: [...nextSkipped], screen: screen + 1, tiebreakerChecked: checked,
          answers: Object.fromEntries(Object.entries(groupDraftRef.current!.snapshot.answers).filter(([id]) => activeIds.has(Number(id)))),
          answeredQuestionIds: [...answeredQuestionsRef.current].filter(id => activeIds.has(id)) });
        if (groupDraftRef.current) saveQuizSession(groupDraftSession(groupDraftRef.current));
      }
      // Always proceed to next screen (we handle button visibility separately now)
      setScreen(screen + 1);
    
      // Ensure scroll to top after screen change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    } catch (error) {
      if (generationRef.current === generation) setSkipWarning(error instanceof Error ? error.message : 'Could not load the next questions. Try again.');
    } finally {
      if (generationRef.current === generation) { transitionRef.current = false; setTransitionBusy(false); }
    }
  };

  // Long-quiz transitions are one-way. Leaving screen 1 runs the tiebreaker
  // check that can swap questions 25-36, and leaving screen 2 locks Phase 1
  // and loads the macro-cell-targeted Phase 2 set. Both decisions run exactly
  // once, so re-editing the answers they were derived from would silently
  // desync the question set from the answers. Back therefore stops at the
  // last boundary crossed: 1 -> 0 and 4 -> 3 remain possible, 2 -> 1 and
  // 3 -> 2 do not. Short and group-short quizzes have one screen, so the
  // floor of 0 changes nothing there.
  const minBackScreen = quizType === 'long' ? (screen >= 3 ? 3 : screen >= 2 ? 2 : 0) : 0;

  const handleBack = () => {
    if (screen > minBackScreen) {
      commitGroupProgress({ screen: screen - 1 });
      setScreen(screen - 1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current || groupSubmissionRef.current) {
      debugLog('⏳ Submission already in progress, ignoring duplicate submit');
      return;
    }
    const submissionGeneration = generationRef.current;

    // Untouched questions score as neutral, so submitting without moving a
    // single slider produced a real-looking 0/0/0 result and the centrist
    // archetype. ~1.4% of short-quiz rows were exactly [0.5 x 12], and their
    // median time from quiz_session_started to quiz_results_viewed was one
    // second against 126 seconds for everyone else. Neutral is a legitimate
    // answer, so this gates only the case where nothing at all was touched.
    const touchedCount = questions.filter(q =>
      (answers[q.id] !== undefined && answers[q.id] !== null) || skippedQuestions.has(q.id)
    ).length;
    if (touchedCount === 0) {
      setSkipWarning('Answer at least one question before seeing your results. Drag any slider toward agree or disagree, or skip the ones you would rather not answer.');
      setTimeout(() => setSkipWarning(null), 6000);
      capturePosthogEvent(posthog, 'quiz_submit_blocked_empty', { quiz_type: quizType });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    debugLog('🏁 Quiz Complete!');
    const answeredCount = questions.length - skippedQuestions.size;
    const skippedCount = skippedQuestions.size;
    debugLog(`📊 Final Quiz: ${answeredCount} questions answered, ${skippedCount} skipped (${quizType === 'long' ? 'Phase 1: 36, Phase 2: 24' : 'Short quiz: 12'})`);

    // A restored submission already crossed this event boundary.
    const alreadyCompleted = groupDraftRef.current?.snapshot.tracked.completed;
    if (groupDraftRef.current && !alreadyCompleted) {
      persistGroupDraft({ ...groupDraftRef.current, updatedAt: Date.now(),
        snapshot: { ...groupDraftRef.current.snapshot, tracked: { ...groupDraftRef.current.snapshot.tracked, completed: true } },
        submission: groupDraftRef.current.submission ?? { phase: 'preparing' } });
    }
    if (!alreadyCompleted) capturePosthogEvent(posthog, 'quiz_completed', {
      quiz_type: quizType,
      questions_answered: answeredCount,
      questions_skipped: skippedCount,
      total_questions: questions.length,
      completion_rate: (answeredCount / questions.length) * 100
    }, { sendToServer: true });

    // Mark session as complete locally (for cleanup)
    completeSession();

    // Prepare data for results page
    // Filter out skipped questions entirely, but keep answers for non-skipped
    const questionData = questions
      .filter(q => !skippedQuestions.has(q.id)) // Remove skipped questions
      .map(q => ({
        id: q.id,
        originalId: q.originalId, // TSV id_code; lets compare flows map answers without the TSV
        axis: q.axis,
        agreeDir: q.agreeDir || 1,
        answer: answers[q.id], // Only answered questions remain
        // Include supplementAxis for Phase 2 questions
        supplementAxis: isPhase2Question(q) ? q.supplementAxis : undefined
      }));

    // DEBUG: Log submission data structure
    debugLog('🚀 SUBMITTING DATA (New Approach):');
    debugLog(`📊 Total questions: ${questions.length}`);
    debugLog(`📊 Skipped questions: ${skippedQuestions.size}`);
    debugLog(`📊 Questions being submitted: ${questionData.length}`);
    debugLog('📋 Skipped question IDs:', Array.from(skippedQuestions));
    debugLog('📋 Sample submitted data (first 10):');
    questionData.slice(0, 10).forEach((q, i) => {
      debugLog(`  [${i}] Q${q.id}: ANSWERED (${q.answer}) axis=${q.axis} suppAxis=${q.supplementAxis}`);
    });

    // Include skip statistics for analytics
    const skipStats = {
      totalSkipped: skippedCount,
      skipsByAxis: {
        economic: 0,
        governance: 0,
        social: 0
      }
    };

    skippedQuestions.forEach(qId => {
      const question = questions.find(q => q.id === qId);
      if (question && question.id < 45) {
        skipStats.skipsByAxis[question.axis]++;
      }
    });

    const submissionData = {
      questionData,
      skipStats
    };

    const encodedData = btoa(JSON.stringify(submissionData));
    const debugParam = isDebugMode ? '&debug=true' : '';
    const answersArray = questionData.map(q => q.answer ?? 0.5);
    const questionIds = questionData.map(q => q.id);

    if (typeof window !== 'undefined') {
      (window as Window & { __skipStats?: typeof skipStats }).__skipStats = skipStats;
    }

    const prepareResult = async () => {
      const scores = await calculateScores(answersArray, questionIds, quizType, undefined, false, questionData);
      const gridData = await loadGridData(quizType as 'short' | 'long');
      let ideologyForSave = null;

      if (quizType === 'long') {
        const hasSupplementary = scores.supplementary && Object.keys(scores.supplementary).length > 0;
        if (hasSupplementary) {
          // The supplementary answers belong to the macro cell whose Phase 2
          // questions were served, encoded in the score keys (EMGL-C etc).
          // Match the micro ideology within THAT cell rather than re-deriving
          // a cell from the final scores: near a boundary the two can differ,
          // and a result saved with a cell that doesn't match its
          // supplementary keys renders all-zero supplement bars.
          const phase2Cell = macroCellFromSupplementKeys(scores.supplementary);
          ideologyForSave = phase2Cell
            ? await findDetailedIdeologyWithPredefinedMacroCell(gridData, phase2Cell, scores.supplementary)
            : await findDetailedIdeologyWithSupplementary(
                gridData,
                scores.economic,
                scores.governance,
                scores.supplementary
              );
        }
        if (!ideologyForSave) {
          ideologyForSave = findDetailedIdeology(gridData, scores.economic, scores.governance, scores.social);
        }
      } else {
        ideologyForSave = findIdeologyByPosition(gridData, scores.economic, scores.governance);
      }

      const macroCellCode = ideologyForSave?.macroCellCode || macroCellFromScores(scores.economic, scores.governance);

      // Persist position+macro cell on the local session so the past-self
      // comparison page (/results/history) can render across quizzes,
      // including short quizzes that don't otherwise enter Phase 2.
      // Matching above can outlive navigation into another attempt. The
      // legacy helper writes the current session, so guard before touching it.
      if (isGroupMode && generationRef.current !== submissionGeneration) throw new GroupResumeError(401, 'identity_changed', 'This quiz attempt is no longer active.');
      updateSessionPhase1Results(
        { economic: scores.economic, governance: scores.governance, social: scores.social },
        macroCellCode,
      );

      const alignmentVector = findVisionAlignment(
        toVisionScale(scores.economic),
        toVisionScale(scores.governance),
        toVisionScale(scores.social)
      );
      const alignmentLabel = quizType === 'short'
        ? (ideologyForSave?.macroCellLabel || alignmentVector.label)
        : (ideologyForSave?.ideology || alignmentVector.label);
      const alignmentDescription = ideologyForSave?.explanation || alignmentVector.description;
      const hasSupplementary = quizType === 'long' && scores.supplementary && Object.keys(scores.supplementary).length > 0;
      const phase = hasSupplementary ? 2 : 1;

      // originalId -> answer map for every answered question. Persisted
      // inside `result` (result_json round-trips on both data backends,
      // unlike top-level questionData which the D1 store drops) so a
      // compare-group creator's slot can be populated from their existing
      // result without a retake. extractLockedAnswers filters this to the
      // group's locked set.
      const allAnswersByOriginalId: Record<string, number> = {};
      for (const q of questions) {
        const a = answers[q.id];
        if (a === undefined || a === null) continue;
        if (skippedQuestions.has(q.id)) continue;
        allAnswersByOriginalId[q.originalId || `Q${q.id}`] = a;
      }

      const result: Parameters<typeof saveQuizResult>[0] = {
        answers: answersArray,
        quizType: quizType as 'short' | 'long',
        questionData,
        skipStats,
        result: {
          economicScore: scores.economic,
          governanceScore: scores.governance,
          socialScore: scores.social,
          alignmentLabel,
          alignmentDescription,
          macroCellCode,
          ...(hasSupplementary && { supplementaryScores: scores.supplementary }),
          gridPosition: {
            economic: scores.economic,
            social: scores.social
          },
          answersByOriginalId: allAnswersByOriginalId,
          // D1 drops top-level questionData (no column); nest a copy inside
          // result_json (like answersByOriginalId) so the paid report keeps
          // its answer-derived sections when loading by ?id=.
          questionData: questionData.map(q => ({ ...q, answer: q.answer ?? null }))
        },
        phase
      };

      const answersByOriginalId: Record<string, number> = {};
      if (isGroupMode && groupId) {
        let lockedIds: Set<string> | null = null;
        try {
          const group = await getGroup(groupId);
          lockedIds = group ? new Set(group.questionIds) : null;
        } catch (err) {
          debugError('getGroup for contribute failed:', err);
        }

        for (const q of questions) {
          const a = answers[q.id];
          if (a === undefined || a === null) continue;
          if (skippedQuestions.has(q.id)) continue;
          const key = q.originalId || `Q${q.id}`;
          // If we know the locked set, only contribute answers from it.
          // Falling back to "include all" if lookup failed keeps short-mode
          // working unchanged.
          if (lockedIds && !lockedIds.has(key)) continue;
          answersByOriginalId[key] = a;
        }
      }
      return {
        result,
        completion: {
          answers: answersByOriginalId,
          cubePosition: { x: scores.economic, y: scores.governance, z: scores.social },
          archetype: alignmentLabel,
        },
      };
    };

    const savePersonalResult = async (result: Parameters<typeof saveQuizResult>[0], onAcknowledged?: (id: string) => void) => {
      if (isGroupMode && generationRef.current !== submissionGeneration) throw new GroupResumeError(401, 'identity_changed', 'This quiz attempt is no longer active.');
      const docId = await saveQuizResult(result, onAcknowledged);
      if (!docId) throw new Error('Personal result save returned no ID');
      capturePosthogEvent(posthog, 'quiz_result_saved', {
        quiz_type: quizType,
        document_id: docId,
        answers_count: answersArray.length,
        skipped_count: skipStats.totalSkipped,
        phase: result.phase === 2 ? 'phase2' : 'phase1'
      }, { sendToServer: true });
      return docId;
    };

    if (isGroupMode && groupId) {
      const generation = generationRef.current;
      const expectedScope = groupDraftRef.current!.scope;
      let alreadyContributed = false;
      const validateCurrentGroup = async (savedId?: string) => {
        const context = await loadGroupResumeContext(groupId);
        if (generationRef.current !== generation || context.uid !== expectedScope.uid) throw new GroupResumeError(401, 'identity_changed', 'Your browser session has changed.');
        const committed = !!savedId && context.personalResultId === savedId;
        if (!committed) assertGroupResumeContext(context, quizType, expectedScope);
        return committed;
      };
      groupSubmissionRef.current = createGroupSubmission({
        groupId,
        quizType,
        prepare: prepareResult,
        initialCheckpoint: groupDraftRef.current?.submission,
        onCheckpoint: checkpoint => {
          if (generationRef.current !== generation || !groupDraftRef.current) return;
          persistGroupDraft({ ...groupDraftRef.current, updatedAt: Date.now(), submission: checkpoint });
        },
        beforeAttempt: async () => {
          alreadyContributed = await validateCurrentGroup(groupDraftRef.current?.submission?.personalResultId);
        },
        savePersonalResult: async (result, onAcknowledged) => {
          // Preparation may await data after the entry check. Revalidate the
          // current cookie immediately before saving these scoped answers.
          await validateCurrentGroup();
          return savePersonalResult(result, onAcknowledged);
        },
        contribute: async (id, completion) => {
          // The ancillary percentage request can also outlive an identity or
          // membership change. Never send the previous identity's completion.
          alreadyContributed = await validateCurrentGroup(completion.personalResultId);
          if (!alreadyContributed) await contributeAnswers(id, completion);
        },
        personalResultUrl: personalGroupResultUrl,
        onState: state => {
          if (generationRef.current !== generation) return;
          setGroupSubmissionState(state.reason === 'identity_changed' ? { ...state, personalResultUrl: undefined } : state);
          setIsSubmitting(state.phase !== 'failed' && state.phase !== 'complete');
        },
        onEvent: (event, properties) => capturePosthogEvent(posthog, event, properties, { sendToServer: true }),
        onSuccess: personalResultUrl => {
          if (generationRef.current !== generation) return;
          clearGroupDraft(expectedScope);
          groupDraftRef.current = null;
          if (!alreadyContributed) capturePosthogEvent(posthog, 'group_quiz_completed', {
            group_id: groupId,
            quiz_type: quizType,
          }, { sendToServer: true });
          // Long finishers earned the full personal result. Short finishers
          // enter the lobby only after their contribution is acknowledged.
          router.push(quizType === 'long' ? personalResultUrl : `/group/${groupId}`);
        },
      });
      await groupSubmissionRef.current.run(!!alreadyCompleted);
      return;
    }

    try {
      const { result } = await prepareResult();
      const docId = await savePersonalResult(result);
      router.push(`/quiz/results?id=${docId}&type=${quizType}${debugParam}`);
    } catch (error) {
      debugError('❌ Failed to pre-save quiz result, falling back to encoded data:', error);
      router.push(`/quiz/results?data=${encodeURIComponent(encodedData)}&type=${quizType}${debugParam}`);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const startIdx = screen * QUESTIONS_PER_SCREEN;
  const endIdx = startIdx + QUESTIONS_PER_SCREEN;
  const currentQuestions = questions.slice(startIdx, endIdx);

  // Progress counts questions the user has dealt with: a real answer or an
  // explicit skip. Raw Object.keys(answers).length also counted the null
  // entries the skip flow writes, so a skipped question moved the bar while
  // a carried-over skip (Set membership only) did not.
  const numAnswered = questions.filter(q =>
    (answers[q.id] !== undefined && answers[q.id] !== null) || skippedQuestions.has(q.id)
  ).length;

  // Debug mode auto-fill functions (kept for manual debug invocation via console)
  const _autoFillAll = (value: number) => {
    const newAnswers: Record<number, AnswerValue> = {};
    questions.forEach(q => {
      newAnswers[q.id] = value;
    });
    setAnswers(newAnswers);
  };

  const _autoFillRandom = () => {
    const newAnswers: Record<number, AnswerValue> = {};
    questions.forEach(q => {
      newAnswers[q.id] = Math.random();
    });
    setAnswers(newAnswers);
  };

  const _autoFillPattern = (pattern: 'alternate' | 'extreme') => {
    const newAnswers: Record<number, AnswerValue> = {};
    questions.forEach((q, index) => {
      if (pattern === 'alternate') {
        newAnswers[q.id] = index % 2 === 0 ? 0.2 : 0.8;
      } else if (pattern === 'extreme') {
        newAnswers[q.id] = Math.random() > 0.5 ? 0.05 : 0.95;
      }
    });
    setAnswers(newAnswers);
  };
  void _autoFillAll;
  void _autoFillRandom;
  void _autoFillPattern;

  // Calculate live scores in debug mode
  useEffect(() => {
    if (!isDebugMode || Object.keys(answers).length === 0) return;

    const calculateLiveScores = async () => {
      try {
        const answersArray = questions.map(q => answers[q.id] ?? 0.5);
        const questionIds = questions.map(q => q.id);
        const questionData = questions.map(q => ({
          id: q.id,
          axis: q.axis,
          agreeDir: q.agreeDir || 1,
          supplementAxis: isPhase2Question(q) ? q.supplementAxis : undefined
        }));
        
        const scores = await calculateScores(answersArray, questionIds, quizType, undefined, false, questionData);
        setLiveScores(scores);
      } catch (error) {
        debugError('Error calculating live scores:', error);
      }
    };

    calculateLiveScores();
  }, [answers, questions, isDebugMode, quizType]);
  
  // Total expected questions for each quiz type
  const totalExpectedQuestions = quizType === 'long' ? 60 : 12; // 36 Phase 1 + 24 Phase 2 for long, 12 for short
  
  // Progress should always be out of expected total
  const progress = (numAnswered / totalExpectedQuestions) * 100;

  if (groupSubmissionState) {
    return <GroupSubmissionRecovery state={groupSubmissionState} storageUnavailable={storageUnavailable} groupUrl={`/group/${groupId}`} onRestart={restartGroup}
      onRetry={() => { if (groupSubmissionRef.current) void groupSubmissionRef.current.run(true); else void handleSubmit(); }} />;
  }

  if (isGroupMode && resumeProblem) {
    return (
      <main className="min-h-screen bg-background p-6 flex items-center justify-center">
        <section className="max-w-lg space-y-5 rounded-2xl border border-slate-200 p-6">
          <div role="alert">
            <h1 tabIndex={-1} ref={element => { element?.focus(); }} className="text-2xl font-semibold outline-none">Your group quiz progress</h1>
            <p className="mt-3">{resumeProblem.message}</p>
          </div>
          {expiredRemovalFailed && <p role="status">This browser could not remove the expired saved progress. Reload recovery is unavailable.</p>}
          {resumeProblem.retryable && <Button className="px-5" onClick={() => setRestartVersion(value => value + 1)}>Try again</Button>}
          {resumeProblem.restartable && <button className="block underline underline-offset-4" onClick={restartGroup}>Start over</button>}
          {resumeProblem.personalResultUrl && <a className="block underline underline-offset-4" href={resumeProblem.personalResultUrl}>View your personal result</a>}
          <a className="block underline underline-offset-4" href={`/group/${groupId}`}>Return to the group</a>
        </section>
      </main>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-linear-to-b from-background to-primary/25 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500 mx-auto"></div>
          <p className="text-xl text-foreground">{t(locale, 'loadingQuestions')}</p>
          <p className="text-sm text-slate-600">{t(locale, 'loadingMobile')}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <div className="min-h-screen bg-linear-to-b from-background to-primary/25 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-lg">
          <p className="text-xl text-red-600 font-semibold">{t(locale, 'failedToLoad')}</p>
          <p className="text-slate-700">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-[#2a4173] transition-colors"
          >
            {t(locale, 'tryAgain')}
          </button>
          <p className="text-sm text-slate-600 mt-4">{t(locale, 'persistError')}</p>
        </div>
      </div>
    );
  }

  // Show message if no questions loaded
  if (!isLoading && questions.length === 0) {
    return (
      <div className="min-h-screen bg-linear-to-b from-background to-primary/25 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-xl text-slate-700">{t(locale, 'noQuestions')}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-[#2a4173] transition-colors"
          >
            {t(locale, 'reloadPage')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-background to-primary/25 p-4 md:p-8 overflow-x-hidden">
      <div className="max-w-3xl mx-auto space-y-12">
        {isGroupMode && compareLabel && (
          <div className="rounded-xl bg-[#2a4173] text-white px-4 py-3 text-sm font-medium text-center shadow">
            You&apos;re comparing with {compareLabel}. Finish to see where you line up.
          </div>
        )}
        {/* A host who started answering before sending the link had no way to
            send it without abandoning the quiz. The lobby is the only other
            place the invite exists, and last month only 34 of 55 hosts ever
            shared one. Copying here keeps them in the flow. */}
        {isGroupMode && groupId && (
          <div className="rounded-xl border border-[#2a4173]/20 bg-[#2a4173]/5 px-4 py-3 text-sm text-center">
            <span className="text-foreground/75">Nobody else in yet? </span>
            <button
              type="button"
              onClick={async () => {
                const url = `${window.location.origin}/group/${groupId}`;
                try {
                  await navigator.clipboard.writeText(url);
                  setInviteCopied(true);
                  setTimeout(() => setInviteCopied(false), 2500);
                  capturePosthogEvent(
                    posthog,
                    'group_invite_copied',
                    { group_id: groupId, method: 'in_quiz' },
                    { sendToServer: true },
                  );
                } catch {
                  window.prompt('Copy the invite link:', url);
                }
              }}
              className="font-semibold text-[#2a4173] underline underline-offset-2 hover:text-[#1f3258]"
            >
              {inviteCopied ? 'Invite link copied' : 'Copy the invite link'}
            </button>
          </div>
        )}
        {isGroupMode && (
          <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm space-y-2">
            {restoredProgress && <p role="status">Your progress was restored in this tab.</p>}
            {storageUnavailable && <p role="status">Reload recovery is unavailable in this tab. Keep this page open.</p>}
            <button type="button" onClick={restartGroup} className="font-medium underline underline-offset-4">Start over</button>
          </div>
        )}
        {/* Progress Bar */}
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-6">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(to right, #93a8d1, #3b5998)'
            }}
          />
        </div>
        {carriedOverCount > 0 && (
          <div className="rounded-xl bg-primary/15 text-secondary px-4 py-3 text-sm font-medium text-center">
            Your {carriedOverCount} short-quiz answers carried over, so you&apos;re
            already {Math.round((carriedOverCount / totalExpectedQuestions) * 100)}% done.
            Adjust any of them as you go.
          </div>
        )}
        <div className="flex justify-end mb-2">
          <LanguageSwitcher onLocaleChange={() => {
            if (isGroupMode) { generationRef.current++; groupDraftRef.current = null; setIsLoading(true); }
            setAnswers({});
            setSkippedQuestions(new Set());
            setScreen(0);
          }} />
        </div>
        <h1 className="text-4xl font-bold text-foreground text-center mb-8">
          {quizType === 'long' ? t(locale, 'longQuizTitle') : t(locale, 'shortQuizTitle')}
        </h1>


        <div className="space-y-16 md:space-y-20">
          {currentQuestions.map((question) => {
            const isSkipped = skippedQuestions.has(question.id);
            return (
              // Skipped state used to combine `opacity-50` on the container
              // AND `text-slate-400` on the heading, which compounded into an
              // effectively near-white ~#caced5. User reported it as "near
              // white / hard to read." Now we mute via container opacity 70
              // and keep the heading at full foreground color, so the
              // question stays legible while still reading as de-emphasized.
              <div key={question.id} className={`space-y-4 transition-opacity duration-200 ${isSkipped ? 'opacity-70' : ''}`}>
                {/* Statement with explanation and skip icons */}
                <div className="flex items-center gap-3 mb-6 px-4 md:px-8">
                  <div className="max-w-2xl mx-auto flex items-center gap-3">
                    <h2 className="text-xl md:text-2xl font-semibold text-center leading-relaxed flex-1 text-foreground">
                      {question.question}
                    </h2>

                    {/* Icons container - stacked vertically */}
                    <div className="flex flex-col gap-1">
                      {/* Info icon with tooltip */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className={`shrink-0 p-2 transition-colors rounded-full touch-manipulation ${
                                isSkipped
                                  ? 'text-slate-500 cursor-not-allowed'
                                  : 'text-slate-500 hover:text-slate-700 hover:bg-gray-100'
                              }`}
                              disabled={isSkipped}
                              aria-label={t(locale, 'showExplanation')}
                              onClick={() => console.log('Info button clicked', { questionId: question.id, description: question.description })}
                            >
                              <Info size={20} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            className="w-[min(28rem,calc(100vw-6rem))] text-sm leading-relaxed"
                            sideOffset={8}
                          >
                            {question.description || `Test tooltip for question ${question.id}`}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {/* Skip/Unskip icon */}
                      {isSkipped ? (
                        <button
                          onClick={() => {
                            const newSkipped = new Set(skippedQuestions);
                            newSkipped.delete(question.id);
                            commitGroupProgress({ skippedQuestionIds: [...newSkipped] });
                            setSkippedQuestions(newSkipped);
                            updateSessionSkips([...newSkipped]);
                            // Set to neutral if no previous answer
                            if (answers[question.id] === null || answers[question.id] === undefined) {
                              handleAnswerSelect(question.id, 0.5);
                            }
                          }}
                          className="p-2 transition-colors rounded-full hover:bg-green-100 touch-manipulation text-slate-500 hover:text-green-600"
                          aria-label={t(locale, 'unskipQuestion')}
                        >
                          <Check size={20} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSkipQuestion(question.id)}
                          className="p-2 transition-colors rounded-full hover:bg-red-100 touch-manipulation text-slate-500 hover:text-red-500"
                          aria-label={t(locale, 'skipQuestion')}
                        >
                          <Ban size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show slider only when not skipped */}
                {!isSkipped && (
                  <div className="relative pt-10 md:pt-12 px-4 md:px-8">
                    {/* Current selection label - moved higher and made more prominent */}
                    {answers[question.id] !== undefined && answers[question.id] !== null && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2">
                        <span className="inline-block bg-primary/20 text-secondary font-semibold px-6 py-1.5 rounded-full text-sm md:text-base whitespace-nowrap">
                          {getValueLabelTranslated(locale, answers[question.id]!)} • {getPercentageDisplay(answers[question.id]!)}
                        </span>
                      </div>
                    )}
                    <div className="relative max-w-2xl mx-auto">
                      {/* Labels above slider */}
                      <div className="flex justify-between mb-2 text-xs md:text-sm text-foreground/75">
                        <span>{t(locale, 'stronglyDisagree')}</span>
                        <span>{t(locale, 'stronglyAgree')}</span>
                      </div>
                  <div className="relative h-8 md:h-10">
                    {/* Slider track with extended click area */}
                    <div 
                      className="absolute top-1/2 left-0 right-0 -translate-y-1/2 cursor-pointer select-none pt-8 pb-8 px-10 -mx-10"
                      style={{
                        // Vertical pans belong to the page, so scrolling
                        // over a slider scrolls. See handleSliderPointerDown.
                        touchAction: 'pan-y',
                        WebkitTouchCallout: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                      data-question-id={question.id}
                      onPointerDown={(e) => handleSliderPointerDown(question.id, e)}
                      onPointerMove={handleSliderPointerMove}
                      onPointerUp={handleSliderPointerUp}
                      onPointerCancel={handleSliderPointerCancel}
                      onMouseEnter={() => setHoveredQuestion(question.id)}
                      onMouseLeave={() => setHoveredQuestion(null)}
                    >
                      {/* Slider track */}
                      <div className="slider-track h-2 bg-slate-200 rounded-full relative overflow-visible absolute top-1/2 left-0 right-0 -translate-y-1/2">
                        {/* Solid color fill based on direction and value */}
                        {answers[question.id] !== undefined && answers[question.id] !== null && (
                          <div
                            className={`absolute h-full ${
                              answers[question.id]! < 0.5 ? 'rounded-l-full' : 'rounded-r-full'
                            } ${
                              dragState?.questionId === question.id && dragState.isDragging ? '' : 'transition-all duration-100'
                            }`}
                            style={{
                              backgroundColor: getSliderColor(answers[question.id]!),
                              left: answers[question.id]! < 0.5 ? `${answers[question.id]! * 100}%` : '50%',
                              right: answers[question.id]! >= 0.5 ? `${(1 - answers[question.id]!) * 100}%` : '50%',
                              willChange: 'left, right, background-color'
                            }}
                          />
                        )}
                        {/* Center line indicator */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-slate-300" />
                        {/* Slider thumb - now with pulsating animation when not set */}
                        {/* NOTE: Tailwind v4 emits `-translate-y-1/2` as the CSS
                            `translate` property (not `transform`). The inline
                            `transform: translateY(-50%)` below stacks ON TOP of
                            it, double-shifting the thumb 10px above the track.
                            Only the inline transform handles centering. */}
                        <div
                          className={`absolute top-1/2 w-6 h-6 md:w-5 md:h-5 bg-slate-600 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing hover:scale-110 ${
                            dragState?.questionId === question.id && dragState.isDragging ? '' : 'transition-transform duration-150'
                          }`}
                          style={{
                            left: `${(answers[question.id] ?? 0.5) * 100}%`,
                            transform: 'translateX(-50%) translateY(-50%)',
                            animation: answers[question.id] === undefined
                              ? 'pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                              : nudgedQuestion === question.id ? 'thumb-nudge 0.5s ease-in-out' : 'none',
                            WebkitTransform: 'translateX(-50%) translateY(-50%)',
                            willChange: 'transform, left',
                            WebkitBackfaceVisibility: 'hidden',
                            backfaceVisibility: 'hidden',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
        </div>
        {/* Safety net: if the user advanced past Phase 1 before Phase 2
            finished loading (e.g. the fetch failed), the screen would be
            empty. Offer a manual retry instead of a blank page. */}
        {quizType === 'long' && !phase2QuestionsLoaded && screen > 0 && currentQuestions.length === 0 && (
          <div className="text-center space-y-4 py-8">
            <p className="text-slate-600">Preparing the questions tailored to your answers so far...</p>
            <button
              onClick={() => void ensurePhase2Loaded()}
              className="px-4 py-2 rounded-lg bg-slate-600 text-white font-semibold hover:bg-[#2a4173] transition-colors"
            >
              Load questions
            </button>
          </div>
        )}
        <div className="flex justify-between items-center mt-8 gap-4">
          <button
            onClick={handleBack}
            disabled={screen <= minBackScreen}
            className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors
              ${screen <= minBackScreen ? 'bg-gray-300 cursor-not-allowed' : 'bg-slate-400 hover:bg-slate-600'}`}
          >
            {t(locale, 'back')}
          </button>
          {/* For long quiz: expect 5 screens (60 questions / 12 per screen) */}
          {/* For short quiz: expect 1 screen (12 questions / 12 per screen) */}
          {(() => {
            if (quizType === 'short') {
              // Short quiz: show Submit on the final screen
              return screen < Math.ceil(questions.length / QUESTIONS_PER_SCREEN) - 1;
            } else {
              // Long quiz: always show Next until we reach the actual final screen
              // Account for Phase 2 loading - if Phase 2 hasn't loaded yet, expect it will
              const expectedTotalQuestions = phase2QuestionsLoaded ? questions.length : 60;
              const expectedTotalScreens = Math.ceil(expectedTotalQuestions / QUESTIONS_PER_SCREEN);
              return screen < expectedTotalScreens - 1;
            }
          })() ? (
            <button
              onClick={handleNext}
              disabled={transitionBusy}
              className="px-6 py-3 rounded-lg font-semibold text-white transition-colors bg-slate-600 hover:bg-[#2a4173]"
            >
              {t(locale, 'next')}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors
                ${isSubmitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-slate-600 hover:bg-[#2a4173]'}`}
            >
              {t(locale, 'seeResults')}
            </button>
          )}
        </div>
        
        <div className="flex justify-center items-center mt-8 pt-4 border-t border-gray-200/30">
          <a 
            href="https://votelyquiz.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-600 transition-colors group"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- inline SVG asset, perf optimization not needed */}
            <img
              src="/votely-cube-v4.svg"
              alt="Votely Logo"
              className="w-6 h-6 group-hover:scale-105 transition-transform"
            />
            <span className="text-sm font-medium">Votely</span>
          </a>
        </div>
      </div>

      {/* New Comprehensive Debug Panel */}
      <DebugPanel
        isVisible={isDebugMode}
        questions={questions}
        answers={answers}
        setAnswers={setAnswers}
        quizType={quizType}
        liveScores={liveScores}
        numAnswered={numAnswered}
        totalExpectedQuestions={totalExpectedQuestions}
        screen={screen}
        totalScreens={TOTAL_SCREENS}
        validationIssues={[]} // Will be populated by enhanced validation
      />

      {/* Toast notification for skip warnings */}
      {skipWarning && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="bg-gray-900 text-white rounded-lg px-6 py-4 shadow-lg max-w-md">
            <p className="text-sm">{skipWarning}</p>
          </div>
        </div>
      )}
    </div>
  );
}
